import { ringCentroid, ringBounds, type Point2 } from '@/site/SiteFootprint';
import {
  MAX_TAG_LABEL_LENGTH,
  MAX_TAG_RADIUS_METERS,
  createSemanticTag,
  type SemanticTag,
} from '@/semantics/SemanticTag';
import { projectReferenceTarget, type ReferenceTag } from './ReferenceTag';
import type { PlanStructure, SemanticCategory, SemanticMap, SemanticRegion } from './SitePipeline';

/**
 * What the reconstruction *learned*, carried onto the site it built.
 *
 * ## The leak this closes
 *
 * Stage 1 is the only part of this application that ever looks at the real place and says what
 * things are. It comes back with 15–40 named regions — "cracked asphalt driveway", "detached
 * garage, cedar shingle", "Acer saccharum, mature, ~12 m" — and the designer's own tags come back
 * alongside them. All of it was used to build the site and then dropped on the floor: the compiler
 * shipped `semanticTags: []`, and the only surviving trace was the project *description*, which no
 * render has ever read.
 *
 * That is the exact information the tag layer exists to preserve. `semantics/SemanticTag.ts` was
 * written because a gravel rectangle beside a bench is a gravel rectangle, and whether it is a
 * bin store or a fire-pit lounge is knowledge that lives nowhere in the geometry. A reconstruction
 * discovers dozens of those answers and then threw every one of them away — so the first render of
 * a site built from a photograph was working from no more than the geometry, having just paid for
 * five calls that named everything in it.
 *
 * ## What carries, and what deliberately does not
 *
 * - **The designer's reference tags carry.** They are the one input nothing else can reconstruct,
 *   and they were authored against this very place. They keep their category, note and emphasis,
 *   and their image coordinates are projected onto the site extent stage 1 fixed.
 * - **Suggestions do not.** A tag still marked `origin: 'suggested'` was a pre-pass model's guess
 *   that nobody reviewed — and stage 1 has already *read* it, weighed it against the pixels, and
 *   re-stated its own conclusion as a region with a polygon. Carrying the guess forward as well
 *   would put an unreviewed inference into the document as though someone had confirmed it, which
 *   is the failure `ReferenceTagOrigin` exists to prevent. Editing a suggestion makes it the
 *   designer's, and an edited one carries like any other.
 * - **Stage 1's regions carry**, targeted as precisely as the pipeline's own `regionId` chain
 *   allows: a region that became a building tags that building, and everything else tags the patch
 *   of ground it occupies.
 * - **`vehicle` regions do not.** They are identified by stage 1 for the express purpose of being
 *   ignored, the site models no cars, and a tag naming one would ask every future render to add one.
 *
 * Pure data and pure functions, deterministic, no clock of its own — ids are derived from the
 * region and tag ids they came from, so recompiling the same artifacts twice produces the same tags
 * rather than churning the document.
 */

/**
 * How many region tags a site may carry.
 *
 * Stage 1 is asked for 15–40 regions and a confused one can return far more. Every tag is read by
 * every future render prompt, so the list is ranked by footprint × confidence and cut — the biggest,
 * most confident things in the photograph are the ones a render most needs named.
 */
export const MAX_REGION_TAGS = 32;

/** How many "things this tool cannot model" reach the document. The tail of that list is noise. */
export const MAX_UNMODELLED_TAGS = 8;

/** Smallest reach a derived tag may claim, so a distant canopy still covers something. */
const MIN_DERIVED_RADIUS_METERS = 0.75;

/** Regions stage 1 finds in order that later stages may skip them. */
const IGNORED_CATEGORIES: ReadonlySet<SemanticCategory> = new Set<SemanticCategory>(['vehicle']);

export interface PipelineSemanticsInput {
  segment: SemanticMap;
  /** Structure plans, read for their `regionId` so a region can tag the object it became. */
  structures?: readonly PlanStructure[];
  /** Ids of objects that were actually built — a plan the compiler dropped must not leave a tag. */
  builtObjectIds?: ReadonlySet<string>;
  /** Tags placed on the reference photograph before the run. */
  referenceTags?: readonly ReferenceTag[];
  /** Overrides `new Date()` so a compiled project is reproducible in tests. */
  now?: Date;
}

/**
 * Every statement the reconstruction can make about the site it just built.
 *
 * Ordered designer-first, then regions by significance, then the unmodelled list — which is the
 * order of how much a reader should trust them, and the order a prompt should read them in.
 */
export function pipelineSemanticTags(input: PipelineSemanticsInput): SemanticTag[] {
  const { segment } = input;
  const projection = { widthMeters: segment.site.widthMeters, lengthMeters: segment.site.lengthMeters };
  const tags: SemanticTag[] = [];
  const seen = new Set<string>();

  const push = (tag: SemanticTag): void => {
    if (tag.label.length === 0 || seen.has(tag.id)) return;
    seen.add(tag.id);
    tags.push(tag);
  };

  /* -- The designer's own marks ---------------------------------------------------- */

  for (const reference of input.referenceTags ?? []) {
    if (reference.origin === 'suggested') continue;
    const { x, z, radiusMeters } = projectReferenceTarget(reference.target, projection);
    push(
      createSemanticTag({
        id: `tag-ref-${reference.id}`,
        target: {
          kind: 'location',
          x: round(x, 2),
          z: round(z, 2),
          radiusMeters: clamp(round(radiusMeters, 2), MIN_DERIVED_RADIUS_METERS, MAX_TAG_RADIUS_METERS),
        },
        category: reference.category,
        label: reference.label,
        emphasis: reference.emphasis,
        ...(reference.note !== undefined ? { note: reference.note } : {}),
        ...(input.now ? { now: input.now } : {}),
      }),
    );
  }

  /* -- What stage 1 identified ------------------------------------------------------ */

  // A region becomes a tag on the object it produced when the structures stage claimed it, which is
  // strictly better than a coordinate: the tag then follows the building if it is ever moved.
  const objectForRegion = new Map<string, string>();
  for (const plan of input.structures ?? []) {
    if (plan.regionId === undefined) continue;
    if (input.builtObjectIds && !input.builtObjectIds.has(plan.id)) continue;
    if (!objectForRegion.has(plan.regionId)) objectForRegion.set(plan.regionId, plan.id);
  }

  for (const region of rankRegions(segment.regions).slice(0, MAX_REGION_TAGS)) {
    const { label, note } = statement(region.label, region.notes);
    const objectId = objectForRegion.get(region.id);
    push(
      createSemanticTag({
        id: `tag-region-${region.id}`,
        target: objectId !== undefined ? { kind: 'structure', objectId } : locationOf(region, projection),
        // Stage 1's label answers exactly one question — what is this thing — which is what
        // `identity` is for. Splitting it across material/condition/use would mean guessing at the
        // register of a sentence the stage was never asked to write in any particular register.
        category: 'identity',
        label,
        emphasis: 'mention',
        ...(note !== undefined ? { note } : {}),
        ...(input.now ? { now: input.now } : {}),
      }),
    );
  }

  /* -- What it saw and could not build ---------------------------------------------- */

  // No geometry to point at — that is the definition of this list — so these are statements about
  // the site. A render can draw a picket fence the model does not contain; the model cannot.
  for (const [index, entry] of segment.unmodelled.slice(0, MAX_UNMODELLED_TAGS).entries()) {
    const { label, note } = statement(entry, undefined);
    push(
      createSemanticTag({
        id: `tag-unmodelled-${index + 1}`,
        target: { kind: 'site' },
        category: 'identity',
        label,
        emphasis: 'mention',
        ...(note !== undefined ? { note } : {}),
        ...(input.now ? { now: input.now } : {}),
      }),
    );
  }

  return tags;
}

/* --------------------------------- Internals --------------------------------- */

/**
 * Regions worth keeping, biggest and most confident first.
 *
 * Area in image space is a fair proxy for how much of the site a thing is, and confidence is stage
 * 1's own view of whether it read the thing correctly. Multiplying them ranks a large certain lawn
 * above a small doubtful shrub, which is the right order to cut a list in.
 */
function rankRegions(regions: readonly SemanticRegion[]): SemanticRegion[] {
  return regions
    .filter((region) => !IGNORED_CATEGORIES.has(region.category) && region.label.trim().length > 0)
    .map((region) => ({ region, weight: imageArea(region.polygon) * Math.max(0.05, region.confidence) }))
    .sort((a, b) => b.weight - a.weight)
    .map((entry) => entry.region);
}

/** A region's place on the ground: the centre of its outline, and a reach that covers it. */
function locationOf(
  region: SemanticRegion,
  projection: { widthMeters: number; lengthMeters: number },
): { kind: 'location'; x: number; z: number; radiusMeters: number } {
  const ring: Point2[] = region.polygon.map(([u, v]) => ({
    x: (u - 0.5) * projection.widthMeters,
    z: (v - 0.5) * projection.lengthMeters,
  }));
  const centre = ring.length > 0 ? ringCentroid(ring) : { x: 0, z: 0 };
  const bounds = ringBounds(ring);
  // Half the mean of the two spans: a reach big enough to be the region without swallowing the
  // whole site when the region happens to be a long thin boundary hedge.
  const span = Number.isFinite(bounds.maxX - bounds.minX)
    ? (bounds.maxX - bounds.minX + (bounds.maxZ - bounds.minZ)) / 4
    : MIN_DERIVED_RADIUS_METERS;
  return {
    kind: 'location',
    x: round(centre.x, 2),
    z: round(centre.z, 2),
    radiusMeters: clamp(round(span, 2), MIN_DERIVED_RADIUS_METERS, MAX_TAG_RADIUS_METERS),
  };
}

/**
 * Split a stage's sentence into a tag's label and note.
 *
 * Stage 1 is asked for specificity — "Acer saccharum, mature, ~12 m, broad oval crown, full autumn
 * colour" is a good answer and far longer than a label may be. `normalizeLabel` would cut it at 64
 * characters and land mid-word, so the cut is made at the last separator that fits and the rest is
 * kept in the note, where the whole sentence still reaches the prompt.
 */
function statement(text: string, notes: string | undefined): { label: string; note?: string } {
  const clean = text.replace(/\s+/g, ' ').trim();
  const trailing = notes?.replace(/\s+/g, ' ').trim();

  if (clean.length <= MAX_TAG_LABEL_LENGTH) {
    return trailing ? { label: clean, note: trailing } : { label: clean };
  }

  const head = clean.slice(0, MAX_TAG_LABEL_LENGTH);
  // A clause break beats a word break: "Acer saccharum, mature, ~12 m tall, broad oval crown" is a
  // label, and the same sentence stopped at the last space that fitted is a sentence with its end
  // torn off. Only honour a break that leaves a label worth reading; otherwise take the hard cut.
  const clause = Math.max(head.lastIndexOf(','), head.lastIndexOf(';'));
  const cut = clause >= 16 ? clause : head.lastIndexOf(' ');
  const label = (cut >= 16 ? head.slice(0, cut) : head).trim();
  const rest = clean.slice(label.length).replace(/^[\s,;]+/, '');
  const note = [rest, trailing].filter((part) => part !== undefined && part.length > 0).join(' — ');
  return note.length > 0 ? { label, note } : { label };
}

/** Shoelace area of an image-space polygon, `0..1`. Used only for ranking. */
function imageArea(polygon: readonly [number, number][]): number {
  if (polygon.length < 3) return 0;
  let total = 0;
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i] as [number, number];
    const b = polygon[(i + 1) % polygon.length] as [number, number];
    total += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(total) / 2;
}

function round(value: number, places: number): number {
  return Math.round(value * 10 ** places) / 10 ** places;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
